/**
 * Last-known project list and locations for the Pictures field path.
 * This is not a full offline database. It remembers projects and locations
 * the signed-in user already loaded for one organization.
 */

import { isOffline } from "./connectivity";
import {
  readOrgScopedJson,
  resolveCacheOrganizationId,
  writeOrgScopedJson,
} from "./orgCacheStorage";

export const PROJECT_FIELD_SNAPSHOT_KEY = "CP_PROJECT_FIELD_SNAPSHOT";

function actorUserId(actor) {
  return actor?.supabase_user_id || "";
}

function actorOrgId(actor) {
  return resolveCacheOrganizationId(actor) || "";
}

function clientNameOf(row) {
  return row?.clients?.client_name || row?.client_name || "";
}

function slimFeature(feature) {
  if (!feature?.project_feature_id) return null;
  return {
    project_feature_id: feature.project_feature_id,
    name: feature.name || "",
    target_completion_date: feature.target_completion_date || null,
    measurements: [],
  };
}

function slimLocation(location, projectId) {
  if (!location?.project_location_id) return null;
  return {
    project_location_id: location.project_location_id,
    project_id: location.project_id || projectId,
    organization_id: location.organization_id || null,
    name: location.name || "",
    sort_order: location.sort_order ?? 0,
    target_completion_date: location.target_completion_date || null,
    is_active: location.is_active !== false,
    features: (location.features || []).map(slimFeature).filter(Boolean),
  };
}

export function normalizeFieldProject(row, actor) {
  if (!row?.project_id) return null;
  const clientName = clientNameOf(row);
  return {
    project_id: row.project_id,
    project_name: row.project_name || row.name || "",
    organization_id: row.organization_id || actorOrgId(actor) || null,
    client_id: row.client_id || null,
    client_name: clientName,
    clients: clientName ? { client_name: clientName } : null,
    status: row.status || null,
    is_active: row.is_active !== false,
    project_number: row.project_number || "",
    estimated_end_date: row.estimated_end_date || null,
    site_address: row.site_address || "",
    notes: row.notes || "",
    created_by: row.created_by || null,
  };
}

function readSnapshot(actor) {
  const organizationId = actorOrgId(actor);
  const userId = actorUserId(actor);
  if (!organizationId || !userId) return null;
  const raw = readOrgScopedJson(PROJECT_FIELD_SNAPSHOT_KEY, organizationId);
  if (!raw || raw.userId !== userId || raw.organizationId !== organizationId) {
    return null;
  }
  if (!Array.isArray(raw.projects)) return null;
  return raw;
}

function writeSnapshot(actor, projects) {
  const organizationId = actorOrgId(actor);
  const userId = actorUserId(actor);
  if (!organizationId || !userId) return;
  writeOrgScopedJson(PROJECT_FIELD_SNAPSHOT_KEY, organizationId, {
    userId,
    organizationId,
    savedAt: new Date().toISOString(),
    projects,
  });
}

export function readCachedFieldProjects(actor) {
  return readSnapshot(actor)?.projects || [];
}

export function readCachedFieldProject(actor, projectId) {
  if (!projectId) return null;
  return readCachedFieldProjects(actor).find((row) => row.project_id === projectId) || null;
}

export function readCachedFieldLocations(actor, projectId) {
  const project = readCachedFieldProject(actor, projectId);
  if (!project || !Array.isArray(project.locations)) return null;
  return project.locations;
}

export function replaceFieldProjects(actor, projects) {
  const previous = readSnapshot(actor)?.projects || [];
  const locationsById = new Map(
    previous.map((row) => [row.project_id, row.locations]),
  );
  const next = (projects || []).map((row) => {
    const slim = normalizeFieldProject(row, actor);
    if (!slim) return null;
    if (Array.isArray(row.locations)) {
      slim.locations = row.locations.map((location) => slimLocation(location, slim.project_id)).filter(Boolean);
    } else if (locationsById.has(slim.project_id)) {
      slim.locations = locationsById.get(slim.project_id);
    }
    return slim;
  }).filter(Boolean);
  writeSnapshot(actor, next);
  return next;
}

export function rememberFieldProject(actor, project) {
  const slim = normalizeFieldProject(project, actor);
  if (!slim) return null;
  const projects = readCachedFieldProjects(actor);
  const existing = projects.find((row) => row.project_id === slim.project_id);
  if (existing?.locations && !Array.isArray(project.locations)) {
    slim.locations = existing.locations;
  }
  const next = projects.filter((row) => row.project_id !== slim.project_id);
  next.push(slim);
  writeSnapshot(actor, next);
  return slim;
}

export function forgetFieldProject(actor, projectId) {
  if (!projectId) return;
  const projects = readCachedFieldProjects(actor).filter((row) => row.project_id !== projectId);
  writeSnapshot(actor, projects);
}

export function rememberFieldLocations(actor, projectId, locations) {
  if (!projectId) return;
  const projects = readCachedFieldProjects(actor);
  const existing = projects.find((row) => row.project_id === projectId) || {
    project_id: projectId,
    project_name: "",
    organization_id: actorOrgId(actor) || null,
    is_active: true,
  };
  const slimLocations = (locations || [])
    .map((location) => slimLocation(location, projectId))
    .filter(Boolean);
  const next = projects.filter((row) => row.project_id !== projectId);
  next.push({ ...existing, locations: slimLocations });
  writeSnapshot(actor, next);
}

export async function loadFieldProjects(actor, loadRemote) {
  if (isOffline()) {
    return { projects: readCachedFieldProjects(actor), fromDevice: true };
  }
  try {
    const projects = await loadRemote();
    replaceFieldProjects(actor, projects);
    return { projects, fromDevice: false };
  } catch (error) {
    const cached = readCachedFieldProjects(actor);
    if (cached.length) {
      return { projects: cached, fromDevice: true };
    }
    throw error;
  }
}

export async function loadFieldProjectRecord(actor, projectId, loadRemote) {
  if (!projectId) {
    if (isOffline()) return { record: null, fromDevice: true };
    const row = await loadRemote();
    return { record: row || null, fromDevice: false };
  }
  if (isOffline()) {
    return { record: readCachedFieldProject(actor, projectId), fromDevice: true };
  }
  try {
    const row = await loadRemote();
    if (row) rememberFieldProject(actor, row);
    else forgetFieldProject(actor, projectId);
    return { record: row || null, fromDevice: false };
  } catch (error) {
    const cached = readCachedFieldProject(actor, projectId);
    if (cached) return { record: cached, fromDevice: true };
    throw error;
  }
}
